import isNumber from 'lodash/isNumber'
import {
  AlertTriangle,
  Check,
  ChevronsUpDown,
  Cpu,
  Database,
  Info,
  LockKeyholeIcon,
  MemoryStick,
} from 'lucide-react'
import { Suspense, useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { DatabaseResourceFallback } from '@/components/form/database-resource-selector/fallback'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { gql } from '@/lib/graphql/gql'
import { useIdCompany, useSuspenseQuery } from '@/lib/hooks'
import { cn } from '@/lib/utils'
import type {
  DatabaseResourceTypesQuery,
  DatabaseResourceTypesQueryVariables,
} from '@/components/form/database-resource-selector/index.types.gen'
import type { DatabaseType } from '@/graphql.types'

const DATABASE_RESOURCE_TYPES_QUERY = gql`
  query DatabaseResourceTypes($idCompany: String!) {
    company(id: $idCompany) {
      id
      sevallaBilling {
        id
        taxPercent
      }
      defaultPaymentMethod {
        id
        expYear
        expMonth
      }
      databaseResourceTypes {
        id
        name
        cpuLimit
        memoryLimit
        storageLimit
        price {
          estimatedMonthlyPrice
        }
      }
    }
  }
`

type Props = {
  fieldKey?: string
  originalStorageLimit?: number
  disableUpgradeAbove?: string
  type?: DatabaseType
}

type DatabaseResourceType = NonNullable<
  NonNullable<DatabaseResourceTypesQuery['company']>['databaseResourceTypes']
>[number]

const getDbTier = (name: string): number | null => {
  const match = name.match(/^db(\d+)$/)
  return match ? parseInt(match[1], 10) : null
}

export const DatabaseResourceSelectorContent = ({
  fieldKey = 'idResourceType',
  originalStorageLimit = 0,
  disableUpgradeAbove,
  type,
}: Props) => {
  const [open, setOpen] = useState(false)
  const { idCompany } = useIdCompany()
  const form = useFormContext()
  const { watch } = form

  const idResourceType = watch(fieldKey as any)

  const { data } = useSuspenseQuery<
    DatabaseResourceTypesQuery,
    DatabaseResourceTypesQueryVariables
  >(DATABASE_RESOURCE_TYPES_QUERY, {
    variables: { idCompany },
  })
  const hasVerifiedCard = Boolean(data?.company?.defaultPaymentMethod?.id)
  const selectedResourceType = data?.company?.databaseResourceTypes?.find(
    (resource) => resource?.id === idResourceType
  )
  const resourceTypes = data?.company?.databaseResourceTypes ?? []
  const disableAboveTier = disableUpgradeAbove
    ? getDbTier(disableUpgradeAbove)
    : null
  const isMemoryConstrainedType = type === 'mysql' || type === 'mariadb'
  const isMemoryConstrainedDb1Selected =
    isMemoryConstrainedType && getDbTier(selectedResourceType?.name ?? '') === 1

  const taxPercent = data?.company?.sevallaBilling?.taxPercent ?? 0

  useEffect(() => {
    if (!idResourceType && data?.company?.databaseResourceTypes?.length) {
      const defaultResource = data.company.databaseResourceTypes.find(
        (resource) => resource?.name === 'db1'
      )
      if (defaultResource) {
        form.setValue(fieldKey as any, defaultResource.id)
      }
    }
  }, [data, idResourceType, form, fieldKey])

  const renderResourceRow = (resource: DatabaseResourceType) => {
    const cpuLimit = (resource?.cpuLimit ?? 0) / 1000
    const storageLimit = (resource?.storageLimit ?? 0) / 1000
    const netPrice = resource?.price?.estimatedMonthlyPrice ?? 0
    const grossPrice = netPrice * (1 + taxPercent / 100)
    const price = Math.round((grossPrice + Number.EPSILON) * 100) / 100
    const memoryLimit = (resource?.memoryLimit ?? 0) / 1000

    return (
      <div className="flex items-center w-full justify-between truncate">
        <div className="flex items-center gap-2">
          {!hasVerifiedCard && resource?.name !== 'db1' ? (
            <LockKeyholeIcon className="size-3.5" />
          ) : null}
          <Badge
            variant="outline"
            className={cn(
              'uppercase',
              !hasVerifiedCard && !resource?.name?.startsWith('h')
                ? 'opacity-50'
                : ''
            )}
          >
            {resource?.name}
          </Badge>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1">
              <Database className="size-4" />
              {storageLimit} GB
            </div>
            <div className="flex items-center gap-1">
              <Cpu className="size-4" />
              {cpuLimit} CPU
            </div>
            <div className="flex items-center gap-1">
              <MemoryStick className="size-4" /> {memoryLimit} GB RAM
            </div>
          </div>
        </div>
        {isNumber(resource?.price?.estimatedMonthlyPrice) && (
          <span
            className={
              !hasVerifiedCard && !resource?.name?.startsWith('h')
                ? 'opacity-50'
                : ''
            }
          >
            {price} USD / month
          </span>
        )}
      </div>
    )
  }

  return (
    <FormField
      control={form.control}
      name={fieldKey as any}
      render={({ field }) => (
        <FormItem className="w-full min-w-0">
          <FormLabel>Resource</FormLabel>
          <FormControl>
            <div className="flex flex-col gap-4 w-full min-w-0">
              <Popover open={open} onOpenChange={setOpen} modal>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    className="w-full justify-between hover:bg-transparent data-[state=open]:bg-transparent min-w-0"
                  >
                    {selectedResourceType ? (
                      <div className="flex items-center justify-between gap-2 w-full min-w-0">
                        {renderResourceRow(selectedResourceType)}
                        <ChevronsUpDown className="shrink-0 opacity-50" />
                      </div>
                    ) : (
                      <div className="flex items-center justify-between w-full">
                        Select a resource type
                        <ChevronsUpDown className="ml-auto opacity-50" />
                      </div>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[var(--radix-popper-anchor-width)] min-w-[400px] p-0">
                  <Command>
                    <CommandInput
                      placeholder="Search resource..."
                      className="h-9"
                    />
                    <CommandList>
                      <CommandEmpty>No resource found</CommandEmpty>
                      {resourceTypes?.map((resource) => {
                        if (!resource) {
                          return null
                        }
                        const isSelected = field.value === resource.id
                        const resourceTier = getDbTier(resource.name ?? '')
                        const isAboveUpgradeLimit =
                          disableAboveTier !== null &&
                          resourceTier !== null &&
                          resourceTier >= disableAboveTier
                        return (
                          <CommandItem
                            className="cursor-pointer"
                            key={resource.id}
                            disabled={
                              isSelected ||
                              (!hasVerifiedCard && resource.name !== 'db1') ||
                              resource.storageLimit < originalStorageLimit ||
                              isAboveUpgradeLimit
                            }
                            onSelect={() => {
                              field.onChange(resource.id)
                              setOpen(false)
                            }}
                          >
                            {renderResourceRow(resource)}
                            <Check
                              className={cn(
                                'ml-auto',
                                isSelected ? 'opacity-100' : 'opacity-0'
                              )}
                            />
                          </CommandItem>
                        )
                      })}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              {disableUpgradeAbove ? (
                <Alert>
                  <Info className="size-4" />
                  <AlertDescription>
                    To upgrade to {disableUpgradeAbove} or above, please contact
                    our support team.
                  </AlertDescription>
                </Alert>
              ) : null}
              {isMemoryConstrainedDb1Selected ? (
                <Alert className="border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-50">
                  <AlertTriangle className="size-4" />
                  <AlertTitle>DB1 is not recommended for production</AlertTitle>
                  <AlertDescription>
                    MySQL and MariaDB databases on DB1 can run out of memory
                    under production load. We suggest choosing DB2 or above to
                    ensure smooth operation.
                  </AlertDescription>
                </Alert>
              ) : null}
            </div>
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  )
}

export const DatabaseResourceSelector = (props: Props) => {
  return (
    <Suspense fallback={<DatabaseResourceFallback />}>
      <DatabaseResourceSelectorContent {...props} />
    </Suspense>
  )
}
